import React, { useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { CONTACT_TITLE, useChoiceList } from 'services/offers/offerChoices';
import translations from 'services/translations/translations';

// The choice that opens a field for a title of the user's own
const OTHER = '__other__';

/**
 * The title of a contact person: one of the titles the admins suggest (in the language of the
 * user) or, with "Muu", any text typed. The title stays free text, so a title saved before is
 * kept as it is and shown as typed. Changes as a field of the form:
 * { target: { name: 'title', value } }.
 */
const ContactTitleField = ({
  disabled,
  label,
  onChange,
  value,
}: {
  disabled?: boolean;
  label: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
  value: string;
}): JSX.Element => {
  const { t } = useTranslation([translations.contacts]);
  // The names of the suggested titles
  const titles = useChoiceList(CONTACT_TITLE).map((choice) => choice.label);
  const [otherChosen, setOtherChosen] = useState(false);

  const current = value ?? '';
  const isOther = otherChosen || (current !== '' && !titles.includes(current));
  const change = (title: string) =>
    onChange({ target: { name: 'title', value: title } });

  return (
    <FormGroup>
      <Label className="label">{label}</Label>
      <Input
        aria-label={label}
        disabled={disabled}
        name="title"
        onChange={(event) => {
          if (event.target.value === OTHER) {
            setOtherChosen(true);
            change(titles.includes(current) ? '' : current);
            return;
          }
          setOtherChosen(false);
          change(event.target.value);
        }}
        type="select"
        value={isOther ? OTHER : current}
      >
        <option value="">-</option>
        {titles.map((title) => (
          <option key={title} value={title}>
            {title}
          </option>
        ))}
        <option value={OTHER}>
          {t(`${translations.contacts}:Other title`)}
        </option>
      </Input>
      {isOther ? (
        <Input
          aria-label={t(`${translations.contacts}:Type title`)}
          autoFocus={otherChosen && current === ''}
          className="mt-2"
          disabled={disabled}
          name="title"
          onChange={(event) => change(event.target.value)}
          placeholder={t(`${translations.contacts}:Type title`)}
          value={current}
        />
      ) : null}
    </FormGroup>
  );
};

export default ContactTitleField;
